import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  DollarSign,
  Download,
  FileText,
  MapPin,
  MessageSquare,
  Send,
  Users,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import {
  useGetProgramQuery,
  usePromoteWaitlistEntryMutation,
  useRequestRosterExportMutation,
  useTriggerCampCommunicationMutation,
} from '../../../state/domainRouterApi';
import { readDomainRouterErrorMessage } from '../../../state/domainRouterError';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import { mapSchoolProgramDetailResponse } from '../camps/viewModels';

/* ── Colour tokens ──────────────────────────────────────────────────── */
const PRIMARY = 'var(--primary, #2697a2)';
const BORDER = 'var(--border, #e5e7eb)';
const TEXT = 'var(--iron-grey, #404f4d)';
const TEXT_SECONDARY = 'var(--text-secondary, #6b7280)';
const AQUAMARINE = 'var(--aquamarine, #28E2A7)';
const CORAL = 'var(--coral, #FF6B6B)';
const STRAWBERRY = 'var(--strawberry, #e53e3e)';

/* ── Shared types for domain response shape ─────────────────────────── */
type ProgramDetail = {
  id?: string;
  name?: string;
  status?: 'draft' | 'published' | 'archived';
  campType?: string;
  gradeMin?: string;
  gradeMax?: string;
  location?: string;
  totalCapacity?: number;
  totalEnrolled?: number;
  totalWaitlisted?: number;
};

type SessionView = {
  id?: string;
  label?: string;
  startDate?: string;
  endDate?: string;
  capacity?: number;
  enrolled?: number;
};

type RegistrationView = {
  id?: string;
  studentName?: string;
  sessionLabel?: string;
  registrationStatus?: 'confirmed' | 'pending' | 'cancelled';
  paymentStatus?: 'paid' | 'pending' | 'overdue' | 'waived';
};

type WaitlistEntry = {
  id?: string;
  position?: number;
  studentName?: string;
  sessionLabel?: string;
  status?: 'waiting' | 'offered' | 'expired';
};

type FinanceSummary = {
  collectedCents?: number;
  outstandingCents?: number;
  refundedCents?: number;
};

type CommunicationsSummary = {
  lastSentAt?: string;
  pendingCount?: number;
};

type IngestHealth = {
  state?: 'healthy' | 'stale' | 'failed';
  lastRefreshAt?: string;
  sourceSystem?: string;
};

type ExportAudit = {
  lastExportAt?: string;
  lastExportFormat?: string;
  lastExportBy?: string;
};

type ProgramDetailResponse = {
  program?: ProgramDetail;
  sessions?: SessionView[];
  registrations?: RegistrationView[];
  waitlist?: WaitlistEntry[];
  financeSummary?: FinanceSummary;
  communicationsSummary?: CommunicationsSummary;
  ingestHealth?: IngestHealth;
  exportAudit?: ExportAudit;
};

/* ── Currency formatter ─────────────────────────────────────────────── */
function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return '$0.00';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/* ── Loading skeleton ───────────────────────────────────────────────── */
function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-6 w-40 animate-pulse rounded bg-gray-100" />
      <div className="h-10 w-64 animate-pulse rounded bg-gray-100" />
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="h-44 animate-pulse rounded-xl bg-gray-100" />
      ))}
    </div>
  );
}

/* ── Error banner ───────────────────────────────────────────────────── */
function ErrorBanner({ title, detail }: { title: string; detail: string }) {
  return (
    <SupportErrorMessage>
      <p className="text-sm font-semibold text-irongrey">{title}</p>
      <p className="mt-1 text-sm text-irongrey/65">{detail}</p>
    </SupportErrorMessage>
  );
}

/* ── Section card wrapper ───────────────────────────────────────────── */
function SectionCard({
  title,
  icon,
  testId,
  actions,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  testId: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-xl border bg-white shadow-sm"
      style={{ borderColor: BORDER }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: BORDER }}>
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-sm font-semibold" style={{ color: TEXT }}>{title}</h2>
        </div>
        {actions ?? null}
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

/* ── Status dot helpers ─────────────────────────────────────────────── */
function StatusDot({ status }: { status?: string }) {
  const color =
    status === 'published' ? AQUAMARINE :
    status === 'draft' ? TEXT_SECONDARY :
    status === 'archived' ? STRAWBERRY :
    TEXT_SECONDARY;

  return (
    <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: TEXT }}>
      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      {status === 'published' ? 'Published' : status === 'draft' ? 'Draft' : status === 'archived' ? 'Archived' : '--'}
    </span>
  );
}

function RegistrationStatusDot({ status }: { status?: string }) {
  const color =
    status === 'confirmed' ? AQUAMARINE :
    status === 'pending' ? CORAL :
    status === 'cancelled' ? STRAWBERRY :
    TEXT_SECONDARY;
  const label =
    status === 'confirmed' ? 'Confirmed' :
    status === 'pending' ? 'Pending' :
    status === 'cancelled' ? 'Cancelled' :
    '--';

  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      <span style={{ color: TEXT }}>{label}</span>
    </span>
  );
}

function PaymentStatusDot({ status }: { status?: string }) {
  const color =
    status === 'paid' ? AQUAMARINE :
    status === 'pending' ? CORAL :
    status === 'overdue' ? STRAWBERRY :
    status === 'waived' ? TEXT_SECONDARY :
    TEXT_SECONDARY;
  const label =
    status === 'paid' ? 'Paid' :
    status === 'pending' ? 'Pending' :
    status === 'overdue' ? 'Overdue' :
    status === 'waived' ? 'Waived' :
    '--';

  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      <span style={{ color: TEXT }}>{label}</span>
    </span>
  );
}

function WaitlistStatusDot({ status }: { status?: string }) {
  const color =
    status === 'waiting' ? CORAL :
    status === 'offered' ? AQUAMARINE :
    status === 'expired' ? STRAWBERRY :
    TEXT_SECONDARY;
  const label =
    status === 'waiting' ? 'Waiting' :
    status === 'offered' ? 'Offered' :
    status === 'expired' ? 'Expired' :
    '--';

  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      <span style={{ color: TEXT }}>{label}</span>
    </span>
  );
}

function IngestStateDot({ state }: { state?: string }) {
  const color =
    state === 'healthy' ? AQUAMARINE :
    state === 'stale' ? CORAL :
    state === 'failed' ? STRAWBERRY :
    TEXT_SECONDARY;
  const label =
    state === 'healthy' ? 'Healthy' :
    state === 'stale' ? 'Stale' :
    state === 'failed' ? 'Failed' :
    'Unknown';

  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: TEXT }}>
      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

/* ── Stat block helper ──────────────────────────────────────────────── */
function StatBlock({ label, value, dotColor }: { label: string; value: string; dotColor?: string }) {
  return (
    <div>
      <div className="flex items-center gap-1.5">
        {dotColor ? (
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: dotColor }} />
        ) : null}
        <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
          {label}
        </p>
      </div>
      <p className="mt-1 text-[24px] font-semibold leading-tight" style={{ color: TEXT }}>{value}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Detail Page
   ═══════════════════════════════════════════════════════════════════════ */

export function SchoolCampsDetailPage() {
  const { campId } = useParams<{ campId: string }>();
  const { data: rawData, isLoading, isError, error } = useGetProgramQuery({ programId: campId ?? '' });

  const [promoteEntry, { isLoading: isPromoting }] = usePromoteWaitlistEntryMutation();
  const [requestExport, { isLoading: isExporting }] = useRequestRosterExportMutation();
  const [triggerComm, { isLoading: isSendingComm }] = useTriggerCampCommunicationMutation();

  const [promotingId, setPromotingId] = useState<string | null>(null);

  const handlePromote = useCallback(async (entryId: string) => {
    setPromotingId(entryId);
    try {
      await promoteEntry({ waitlistEntryId: entryId }).unwrap();
    } catch {
      // error surfaced by RTK
    } finally {
      setPromotingId(null);
    }
  }, [promoteEntry]);

  const handleExport = useCallback(async (format: 'csv' | 'pdf') => {
    if (!campId) return;
    try {
      await requestExport({ programId: campId, format }).unwrap();
    } catch {
      // error surfaced by RTK
    }
  }, [requestExport, campId]);

  const handleTriggerCommunication = useCallback(async () => {
    if (!campId) return;
    try {
      await triggerComm({ programId: campId, audience: 'all', templateKey: 'general-update' }).unwrap();
    } catch {
      // error surfaced by RTK
    }
  }, [triggerComm, campId]);

  if (isLoading) {
    return (
      <main
        data-testid="school-camps-detail"
        data-route-ready="true"
        data-extracted-from="source-faithful:school-camps-v1.0"
        className="min-h-screen bg-[var(--background-light)]"
      >
        <div className="mx-auto max-w-[1440px] space-y-6">
          <LoadingSkeleton />
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main
        data-testid="school-camps-detail"
        data-route-ready="true"
        data-extracted-from="source-faithful:school-camps-v1.0"
        className="min-h-screen bg-[var(--background-light)]"
      >
        <div className="mx-auto max-w-[1440px] space-y-6">
          <NavLink
            to="/school/camps"
            className="inline-flex items-center gap-1 text-sm font-medium transition-colors hover:underline"
            style={{ color: PRIMARY }}
          >
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </NavLink>
          <ErrorBanner
            title="Unable to load camp details"
            detail={readDomainRouterErrorMessage(
              error,
              'The camp program could not be loaded. It may not exist or the backend is unavailable.'
            )}
          />
        </div>
      </main>
    );
  }

  const data = mapSchoolProgramDetailResponse(rawData) as ProgramDetailResponse;
  const program = data.program ?? {};
  const sessions: SessionView[] = Array.isArray(data.sessions) ? data.sessions : [];
  const registrations: RegistrationView[] = Array.isArray(data.registrations) ? data.registrations : [];
  const waitlist: WaitlistEntry[] = Array.isArray(data.waitlist) ? data.waitlist : [];
  const finance = data.financeSummary ?? {};
  const comms = data.communicationsSummary ?? {};
  const ingestHealth = data.ingestHealth ?? {};
  const exportAudit = data.exportAudit ?? {};

  return (
    <main
      data-testid="school-camps-detail"
      data-route-ready="true"
      data-extracted-from="source-faithful:school-camps-v1.0"
      className="min-h-screen bg-[var(--background-light)]"
    >
      <div className="mx-auto max-w-[1440px] space-y-6">
        {/* ── Back link ──────────────────────────────────────────── */}
        <NavLink
          to="/school/camps"
          className="inline-flex items-center gap-1 text-sm font-medium transition-colors hover:underline"
          style={{ color: PRIMARY }}
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </NavLink>

        {/* ── Camp header ────────────────────────────────────────── */}
        <div data-testid="school-camps-detail-header">
          <p
            className="text-[12px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: PRIMARY }}
          >
            Camps
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[24px] font-semibold" style={{ color: TEXT }}>
              {program.name ?? 'Camp Program'}
            </h1>
            <StatusDot status={program.status} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-4 text-sm" style={{ color: TEXT_SECONDARY }}>
            {program.campType ? (
              <span>{program.campType.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</span>
            ) : null}
            {program.gradeMin && program.gradeMax ? (
              <span>Grades {program.gradeMin} - {program.gradeMax}</span>
            ) : null}
            {program.location ? (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" /> {program.location}
              </span>
            ) : null}
          </div>
        </div>

        {/* ── Overview card ──────────────────────────────────────── */}
        <SectionCard
          title="Overview"
          icon={<CheckCircle2 className="h-4 w-4" style={{ color: PRIMARY }} />}
          testId="school-camps-detail-overview"
        >
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <StatBlock
              label="Total Capacity"
              value={String(program.totalCapacity ?? 0)}
              dotColor={AQUAMARINE}
            />
            <StatBlock
              label="Total Enrolled"
              value={String(program.totalEnrolled ?? 0)}
              dotColor={AQUAMARINE}
            />
            <StatBlock
              label="Total Waitlisted"
              value={String(program.totalWaitlisted ?? 0)}
              dotColor={(program.totalWaitlisted ?? 0) > 0 ? CORAL : AQUAMARINE}
            />
            <StatBlock
              label="Sessions"
              value={String(sessions.length)}
            />
          </div>

          {sessions.length > 0 ? (
            <div className="mt-5 border-t pt-4" style={{ borderColor: BORDER }}>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
                Sessions
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {sessions.map((session) => (
                  <div
                    key={session.id ?? session.label}
                    className="rounded-lg border p-3"
                    style={{ borderColor: BORDER }}
                  >
                    <p className="text-sm font-medium" style={{ color: TEXT }}>
                      {session.label ?? 'Session'}
                    </p>
                    <p className="mt-0.5 text-xs" style={{ color: TEXT_SECONDARY }}>
                      {session.startDate ?? '--'} &ndash; {session.endDate ?? '--'}
                    </p>
                    <p className="mt-1 text-xs" style={{ color: TEXT_SECONDARY }}>
                      {session.enrolled ?? 0} / {session.capacity ?? 0} enrolled
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {finance.collectedCents != null || finance.outstandingCents != null ? (
            <div className="mt-5 border-t pt-4" style={{ borderColor: BORDER }}>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
                Financial Summary
              </p>
              <div className="grid gap-6 sm:grid-cols-3">
                <StatBlock
                  label="Collected"
                  value={formatCurrency(finance.collectedCents)}
                  dotColor={AQUAMARINE}
                />
                <StatBlock
                  label="Outstanding"
                  value={formatCurrency(finance.outstandingCents)}
                  dotColor={(finance.outstandingCents ?? 0) > 0 ? CORAL : AQUAMARINE}
                />
                <StatBlock
                  label="Refunded"
                  value={formatCurrency(finance.refundedCents)}
                />
              </div>
            </div>
          ) : null}
        </SectionCard>

        {/* ── Roster card ────────────────────────────────────────── */}
        <SectionCard
          title="Roster"
          icon={<Users className="h-4 w-4" style={{ color: PRIMARY }} />}
          testId="school-camps-detail-roster"
        >
          {registrations.length === 0 ? (
            <p className="py-8 text-center text-sm font-medium" style={{ color: TEXT_SECONDARY }}>
              No registrations yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="min-w-full divide-y text-sm"
                style={{ '--tw-divide-color': BORDER } as React.CSSProperties}
              >
                <thead>
                  <tr>
                    <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                      Student
                    </th>
                    <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                      Session
                    </th>
                    <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                      Status
                    </th>
                    <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                      Payment Status
                    </th>
                  </tr>
                </thead>
                <tbody
                  className="divide-y"
                  style={{ '--tw-divide-color': BORDER } as React.CSSProperties}
                >
                  {registrations.map((reg) => (
                    <tr key={reg.id ?? `${reg.studentName}-${reg.sessionLabel}`} className="transition-colors hover:bg-gray-50">
                      <td className="py-3 pr-4 font-medium" style={{ color: TEXT }}>
                        {reg.studentName ?? '--'}
                      </td>
                      <td className="py-3 pr-4" style={{ color: TEXT }}>
                        {reg.sessionLabel ?? '--'}
                      </td>
                      <td className="py-3 pr-4">
                        <RegistrationStatusDot status={reg.registrationStatus} />
                      </td>
                      <td className="py-3 pr-4">
                        <PaymentStatusDot status={reg.paymentStatus} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* ── Waitlist card ──────────────────────────────────────── */}
        <SectionCard
          title="Waitlist"
          icon={<Clock className="h-4 w-4" style={{ color: PRIMARY }} />}
          testId="school-camps-detail-waitlist"
        >
          {waitlist.length === 0 ? (
            <p className="py-8 text-center text-sm font-medium" style={{ color: TEXT_SECONDARY }}>
              No waitlisted entries.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="min-w-full divide-y text-sm"
                style={{ '--tw-divide-color': BORDER } as React.CSSProperties}
              >
                <thead>
                  <tr>
                    <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                      Position
                    </th>
                    <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                      Student
                    </th>
                    <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                      Session
                    </th>
                    <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                      Status
                    </th>
                    <th className="pb-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className="divide-y"
                  style={{ '--tw-divide-color': BORDER } as React.CSSProperties}
                >
                  {waitlist.map((entry) => (
                    <tr key={entry.id ?? `${entry.position}-${entry.studentName}`} className="transition-colors hover:bg-gray-50">
                      <td className="py-3 pr-4 font-medium" style={{ color: TEXT }}>
                        #{entry.position ?? '--'}
                      </td>
                      <td className="py-3 pr-4 font-medium" style={{ color: TEXT }}>
                        {entry.studentName ?? '--'}
                      </td>
                      <td className="py-3 pr-4" style={{ color: TEXT }}>
                        {entry.sessionLabel ?? '--'}
                      </td>
                      <td className="py-3 pr-4">
                        <WaitlistStatusDot status={entry.status} />
                      </td>
                      <td className="py-3 text-right">
                        {entry.status === 'waiting' && entry.id ? (
                          <button
                            type="button"
                            onClick={() => handlePromote(entry.id!)}
                            disabled={isPromoting && promotingId === entry.id}
                            className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-gray-50 disabled:opacity-50"
                            style={{ borderColor: PRIMARY, color: PRIMARY }}
                          >
                            <ArrowUpRight className="h-3 w-3" />
                            Promote
                          </button>
                        ) : (
                          <span className="text-xs" style={{ color: TEXT_SECONDARY }}>--</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* ── Finance card ───────────────────────────────────────── */}
        <SectionCard
          title="Finance"
          icon={<DollarSign className="h-4 w-4" style={{ color: PRIMARY }} />}
          testId="school-camps-detail-finance"
        >
          <div className="grid gap-6 sm:grid-cols-3">
            <StatBlock
              label="Collected"
              value={formatCurrency(finance.collectedCents)}
              dotColor={AQUAMARINE}
            />
            <StatBlock
              label="Outstanding"
              value={formatCurrency(finance.outstandingCents)}
              dotColor={(finance.outstandingCents ?? 0) > 0 ? CORAL : AQUAMARINE}
            />
            <StatBlock
              label="Refunded"
              value={formatCurrency(finance.refundedCents)}
              dotColor={(finance.refundedCents ?? 0) > 0 ? CORAL : TEXT_SECONDARY}
            />
          </div>
        </SectionCard>

        {/* ── Communications card ────────────────────────────────── */}
        <SectionCard
          title="Communications"
          icon={<MessageSquare className="h-4 w-4" style={{ color: PRIMARY }} />}
          testId="school-camps-detail-communications"
          actions={
            <button
              type="button"
              onClick={handleTriggerCommunication}
              disabled={isSendingComm}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: PRIMARY }}
            >
              <Send className="h-3 w-3" />
              Send Communication
            </button>
          }
        >
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
                Last Sent
              </p>
              <p className="mt-1 text-sm font-medium" style={{ color: TEXT }}>
                {comms.lastSentAt ?? 'Never'}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
                Pending
              </p>
              <div className="mt-1 flex items-center gap-1.5">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: (comms.pendingCount ?? 0) > 0 ? CORAL : AQUAMARINE }}
                />
                <span className="text-sm font-medium" style={{ color: TEXT }}>
                  {comms.pendingCount ?? 0}
                </span>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* ── Ingest Status card ─────────────────────────────────── */}
        <SectionCard
          title="Ingest Status"
          icon={<CheckCircle2 className="h-4 w-4" style={{ color: PRIMARY }} />}
          testId="school-camps-detail-ingest"
        >
          <div className="space-y-3">
            <IngestStateDot state={ingestHealth.state} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
                  Last Refresh
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-sm" style={{ color: TEXT }}>
                  <Clock className="h-3.5 w-3.5" style={{ color: TEXT_SECONDARY }} />
                  {ingestHealth.lastRefreshAt ?? 'No data'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
                  Source System
                </p>
                <p className="mt-1 text-sm" style={{ color: TEXT }}>
                  {ingestHealth.sourceSystem ?? '--'}
                </p>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* ── Export card ────────────────────────────────────────── */}
        <SectionCard
          title="Export & Audit"
          icon={<FileText className="h-4 w-4" style={{ color: PRIMARY }} />}
          testId="school-camps-detail-export"
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleExport('csv')}
                disabled={isExporting}
                className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-gray-50 disabled:opacity-50"
                style={{ borderColor: BORDER, color: TEXT }}
              >
                <Download className="h-3 w-3" />
                CSV
              </button>
              <button
                type="button"
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
                className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-gray-50 disabled:opacity-50"
                style={{ borderColor: BORDER, color: TEXT }}
              >
                <Download className="h-3 w-3" />
                PDF
              </button>
            </div>
          }
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
                Last Export
              </p>
              <p className="mt-1 text-sm" style={{ color: TEXT }}>
                {exportAudit.lastExportAt ?? 'Never'}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
                Format
              </p>
              <p className="mt-1 text-sm" style={{ color: TEXT }}>
                {exportAudit.lastExportFormat?.toUpperCase() ?? '--'}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
                Exported By
              </p>
              <p className="mt-1 text-sm" style={{ color: TEXT }}>
                {exportAudit.lastExportBy ?? '--'}
              </p>
            </div>
          </div>
        </SectionCard>
      </div>
    </main>
  );
}
